import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AdminPortalLayout from './AdminPortalLayout';
import { AdminMe, SearchedUser, searchUsers } from '../lib/admin-portal-api';
import { FaUserPlus, FaCheckCircle, FaBan, FaStar, FaExclamationTriangle } from '../lib/icons';

interface Props { me: AdminMe }

const PAGE_SIZE = 20;

export default function AdminPortalUserLookup({ me }: Props) {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState<SearchedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const [lastQ, setLastQ] = useState('');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await searchUsers(q.trim(), 1, PAGE_SIZE);
      setUsers(res.data);
      setPage(1);
      setPageCount(res.pagination.pageCount);
      setTotal(res.pagination.total);
      setLastQ(q.trim());
      setSearched(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Search failed.');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const res = await searchUsers(lastQ, next, PAGE_SIZE);
      setUsers((prev) => [...prev, ...res.data]);
      setPage(next);
      setPageCount(res.pagination.pageCount);
      setTotal(res.pagination.total);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load more.');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <AdminPortalLayout me={me} active="users">
      <Helmet><title>Users — EatMeat Admin</title></Helmet>

      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">User Lookup</h1>
        <p className="text-sm text-gray-500 mt-1">Search by email or username. Click a row for full detail.</p>
      </div>

      <form onSubmit={handleSearch} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 mb-4 flex gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="email or username…"
          className="flex-1 px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none"
        />
        <button type="submit" disabled={loading}
          className="px-5 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 inline-flex items-start gap-2 mb-4">
          <FaExclamationTriangle className="mt-0.5 text-red-500 shrink-0" /> {error}
        </div>
      )}

      {searched && !loading && users.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
          <FaUserPlus className="text-4xl mx-auto mb-3 text-gray-200" />
          <p className="text-sm text-gray-500">No users match that query.</p>
        </div>
      )}

      {users.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Subscription</th>
                <th className="px-4 py-3">Community</th>
                <th className="px-4 py-3">Referral</th>
                <th className="px-4 py-3 text-right">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/admin-portal/users/${u.id}`} className="block">
                      <div className="font-semibold text-gray-800 truncate max-w-xs">{u.username || '—'}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">{u.email}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    {u.blocked ? (
                      <span className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold"><FaBan /> Blocked</span>
                    ) : u.confirmed ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-semibold"><FaCheckCircle /> Active</span>
                    ) : (
                      <span className="text-amber-600 text-xs font-semibold">Pending email</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.is_premium ? (
                      <span className="inline-flex items-center gap-1 text-purple-700 text-xs font-semibold"><FaStar /> Premium</span>
                    ) : (
                      <span className="text-xs text-gray-400">Free</span>
                    )}
                    {u.subscription_status && (
                      <div className="text-[10px] text-gray-400 mt-0.5">{u.subscription_status}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.is_community_banned ? (
                      <span className="text-red-600 text-xs font-semibold">Banned</span>
                    ) : (
                      <span className="text-xs text-gray-700">{u.community_badge || 'member'}</span>
                    )}
                    {u.is_community_moderator && (
                      <span className="ml-2 text-[10px] font-bold text-primary">MOD</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-700">
                    {u.referral_code_used || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-[11px] text-gray-400">
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <span>Showing {users.length} of {total}</span>
            {page < pageCount && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="px-3 py-1.5 rounded-md bg-gray-50 hover:bg-gray-100 font-semibold text-gray-700 disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        </div>
      )}
    </AdminPortalLayout>
  );
}
